import React, { useState, useEffect } from 'react';
import { Info } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { cn } from '../../lib/utils';
import { fmtDateShort, DELTA_COLORS } from './playerFormat';

// ─── Date helpers ───────────────────────────────────────────────────────────

function startOfDay(d) {
  const copy = new Date(d);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

function addDays(date, n) {
  const copy = new Date(date);
  copy.setDate(copy.getDate() + n);
  return copy;
}

function toISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseISODate(s) {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export const PRESETS = [
  { key: 'yesterday', label: 'Yesterday' },
  { key: '14d', label: '14D' },
  { key: '30d', label: '30D' },
  { key: '90d', label: '90D' },
  { key: 'mtd', label: 'MTD' },
  { key: 'custom', label: 'Custom…' },
];

export const DEFAULT_CUSTOM_PRESET = '30d';

function resolvePresetDates(presetKey, today) {
  switch (presetKey) {
    case 'yesterday': {
      const d = addDays(today, -1);
      return { from: d, to: d };
    }
    case '14d': return { from: addDays(today, -13), to: today };
    case '30d': return { from: addDays(today, -29), to: today };
    case '90d': return { from: addDays(today, -89), to: today };
    case 'mtd': return { from: new Date(today.getFullYear(), today.getMonth(), 1), to: today };
    default: return null;
  }
}

// Resolves a preset key or explicit date-string pair into the full range needed to
// render the bar and fetch data: the window itself, its day count, and the
// immediately preceding period of equal length (for the "compare" toggle).
export function resolveMetricsRange({ preset, dateFrom, dateTo }) {
  const today = startOfDay(new Date());
  let from, to;

  if (preset && preset !== 'custom') {
    ({ from, to } = resolvePresetDates(preset, today));
  } else if (dateFrom && dateTo) {
    from = startOfDay(parseISODate(dateFrom));
    to = startOfDay(parseISODate(dateTo));
  } else {
    ({ from, to } = resolvePresetDates(DEFAULT_CUSTOM_PRESET, today));
  }
  if (to < from) [from, to] = [to, from];

  const days = Math.round((to - from) / 86_400_000) + 1;
  const prevTo = addDays(from, -1);
  const prevFrom = addDays(prevTo, -(days - 1));

  return {
    from, to, days, prevFrom, prevTo,
    fromStr: toISODate(from), toStr: toISODate(to),
    prevFromStr: toISODate(prevFrom), prevToStr: toISODate(prevTo),
  };
}

// Relative % change — for currency/count metrics (NGR, GGR, Turnover, Deposits, ...).
export function pctDelta(cur, prev) {
  if (cur == null || prev == null || prev === 0) return null;
  const pct = ((cur - prev) / Math.abs(prev)) * 100;
  return { pct, unit: 'pct' };
}

// Percentage-point change — for metrics that are already a percentage (ratios).
export function ppDelta(cur, prev) {
  if (cur == null || prev == null) return null;
  return { pct: cur - prev, unit: 'pp' };
}

// ─── Section shell (shared by Standard and Custom mode) ────────────────────

export function StatSection({ label, pill, children }) {
  return (
    <div className="rounded-lg border border-white/10 bg-surface-1 px-4 pt-3 pb-4">
      <div className="flex items-center gap-2 mb-3">
        <p className="text-[9px] text-ink-dim uppercase tracking-widest">{label}</p>
        {pill}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">{children}</div>
    </div>
  );
}

export function SectionPill({ mode, resolved }) {
  if (mode === 'custom' && resolved) {
    return (
      <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold text-brand-blue border border-brand-blue/40 bg-brand-blue/10 whitespace-nowrap">
        {resolved.days} days · {fmtDateShort(resolved.from)} → {fmtDateShort(resolved.to)}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold text-ink-dim border border-white/10 bg-white/[0.03] whitespace-nowrap">
      7d vs 30d
    </span>
  );
}

// ─── Custom-mode single-metric card ─────────────────────────────────────────

export function RangeMetricCard({ label, value, color, delta, loading }) {
  if (loading) {
    return (
      <div className="rounded-lg border border-white/[0.07] bg-white/[0.03] px-4 py-4">
        <p className="text-[11px] text-ink-dim uppercase tracking-wide mb-1.5">{label}</p>
        <div className="h-7 w-20 rounded bg-white/10 animate-pulse" />
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-white/[0.07] bg-white/[0.03] px-4 py-4">
      <p className="text-[11px] text-ink-dim uppercase tracking-wide mb-1.5">{label}</p>
      <p className="text-[28px] leading-none font-bold text-ink tabular-nums" style={color ? { color } : {}}>
        {value}
      </p>
      {delta && isFinite(delta.pct) && (
        <span className="block text-xs font-medium mt-1.5" style={{ color: delta.pct > 0 ? DELTA_COLORS.up : DELTA_COLORS.down }}>
          {delta.pct > 0 ? '↑' : '↓'} {Math.abs(delta.pct).toFixed(delta.unit === 'pp' ? 1 : 0)}{delta.unit === 'pp' ? 'pp' : '%'}
        </span>
      )}
    </div>
  );
}

// ─── Control bar ─────────────────────────────────────────────────────────────

// This page's cards/chips/toggles are hand-rolled elements throughout (matching
// the rest of PlayerProfilePage.js) rather than shadcn/ui primitives, so we keep
// that pattern here — but still need explicit keyboard-focus styling since we
// don't get ui/button.jsx's built-in focus ring for free.
const FOCUS_RING = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cta focus-visible:ring-offset-1 focus-visible:ring-offset-surface-1";

function ModeToggle({ mode, onChange }) {
  return (
    <div className="inline-flex rounded-md border border-white/10 overflow-hidden">
      <button
        type="button"
        onClick={() => onChange('standard')}
        className={cn(
          "px-3 py-1.5 text-xs transition-colors",
          FOCUS_RING,
          mode === 'standard' ? "text-ink font-bold bg-white/5" : "text-ink-dim font-medium hover:text-ink"
        )}
      >
        Standard 7d / 30d
      </button>
      <button
        type="button"
        onClick={() => onChange('custom')}
        className={cn(
          "px-3 py-1.5 text-xs border-l border-white/10 transition-colors",
          FOCUS_RING,
          mode === 'custom' ? "text-ink font-bold bg-white/5" : "text-ink-dim font-medium hover:text-ink"
        )}
      >
        Custom range
      </button>
    </div>
  );
}

function PresetChip({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "px-2.5 py-1 rounded-md text-[11px] font-bold border transition-colors whitespace-nowrap",
        FOCUS_RING,
        active
          ? "text-brand-blue border-brand-blue/50 bg-brand-blue/10"
          : "text-ink-dim border-white/10 hover:text-ink hover:border-white/20"
      )}
    >
      {children}
    </button>
  );
}

function SwitchControl({ checked, onChange, label }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      aria-pressed={checked}
      className={cn("inline-flex items-center gap-2 select-none shrink-0 rounded", FOCUS_RING)}
    >
      <span className={cn("relative inline-flex h-5 w-9 items-center rounded-full transition-colors", checked ? "bg-cta" : "bg-white/15")}>
        <span className={cn("inline-block h-4 w-4 transform rounded-full bg-white transition-transform", checked ? "translate-x-4" : "translate-x-0.5")} />
      </span>
      {label && <span className="text-xs text-ink whitespace-nowrap">{label}</span>}
    </button>
  );
}

function CustomRangeDialog({ open, onOpenChange, initialFrom, initialTo, onApply }) {
  const [df, setDf] = useState(initialFrom || '');
  const [dt, setDt] = useState(initialTo || '');

  useEffect(() => {
    if (open) {
      setDf(initialFrom || '');
      setDt(initialTo || '');
    }
  }, [open, initialFrom, initialTo]);

  const valid = Boolean(df && dt && df <= dt);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Custom range</DialogTitle>
          <DialogDescription>Pick the date range for Performance, Money &amp; Ratios. Both dates are included.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] text-ink-dim uppercase tracking-wide mb-1 block">From</label>
            <Input type="date" value={df} max={dt || undefined} onChange={(e) => setDf(e.target.value)} />
          </div>
          <div>
            <label className="text-[11px] text-ink-dim uppercase tracking-wide mb-1 block">To</label>
            <Input type="date" value={dt} min={df || undefined} onChange={(e) => setDt(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button size="sm" disabled={!valid} onClick={() => { onApply(df, dt); onOpenChange(false); }}>Apply</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// metricsWindow shape: { mode, preset, dateFrom, dateTo, compare, resolved,
//   setMode, setPreset, setCustomRange, setCompare }
export function MetricsWindowBar({ metricsWindow }) {
  const { mode, preset, dateFrom, dateTo, compare, resolved, setMode, setPreset, setCustomRange, setCompare } = metricsWindow;
  const [pickerOpen, setPickerOpen] = useState(false);

  return (
    <div className="rounded-lg border border-white/10 bg-surface-1">
      <div className="flex items-center justify-between gap-4 px-4 py-3 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="text-[11px] text-ink-dim uppercase tracking-widest font-bold">Metrics Window</span>
          <ModeToggle mode={mode} onChange={setMode} />
        </div>
        <span className="text-xs text-ink-dim">Applies to Performance, Money &amp; Ratios</span>
      </div>

      {mode === 'custom' && resolved && (
        <div className="flex items-center justify-between gap-4 px-4 py-3 flex-wrap border-t border-white/10 bg-surface rounded-b-lg">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-1.5 flex-wrap">
              {PRESETS.map((p) => (
                <PresetChip
                  key={p.key}
                  active={preset === p.key}
                  onClick={() => (p.key === 'custom' ? setPickerOpen(true) : setPreset(p.key))}
                >
                  {p.label}
                </PresetChip>
              ))}
            </div>
            <div className="w-px h-5 bg-white/10 hidden sm:block" />
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              className={cn("flex items-baseline gap-1.5 text-xs text-ink hover:text-cta transition-colors rounded", FOCUS_RING)}
            >
              <span className="font-bold">{fmtDateShort(resolved.from)}</span>
              <span className="text-ink-dim">→</span>
              <span className="font-bold">{fmtDateShort(resolved.to)}</span>
              <span className="text-ink-dim ml-1">{resolved.days} days</span>
            </button>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-3.5 w-3.5 text-ink-dim cursor-default shrink-0" />
              </TooltipTrigger>
              <TooltipContent className="max-w-[240px]">
                Both {fmtDateShort(resolved.from)} and {fmtDateShort(resolved.to)} are included ({resolved.days} full days).
                {isSameDay(resolved.to, new Date()) && " Today's figures may still be incomplete — data usually finalizes the next day."}
              </TooltipContent>
            </Tooltip>
            <SwitchControl checked={compare} onChange={setCompare} label={`Compare to previous ${resolved.days} days`} />
          </div>
          <span className="text-xs text-ink-dim shrink-0">Lifetime figures unaffected</span>
        </div>
      )}

      <CustomRangeDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        initialFrom={dateFrom || resolved?.fromStr}
        initialTo={dateTo || resolved?.toStr}
        onApply={setCustomRange}
      />
    </div>
  );
}
